// src/features/admin/components/settings/integrations/IntegrationsPanel.tsx
import React, { useState, useEffect } from 'react';
import { 
  LinkIcon,
  ServerIcon,
  EnvelopeIcon,
  BuildingLibraryIcon,
  DocumentDuplicateIcon,
  MagnifyingGlassIcon,
  KeyIcon,
  CalculatorIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  CogIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import IntegrationsDashboard from './components/IntegrationsDashboard';
import EmailStorageTab from './components/EmailStorageTab';
import DocumentStorageTab from './components/DocumentStorageTab';
import APIKeysTab from './components/APIKeysTab';
import CourtSystemsTab from './components/CourtSystemsTab';
import EFilingTab from './components/EFilingTab';
import LegalResearchTab from './components/LegalResearchTab';
import AccountingTab from './components/AccountingTab';
import CalendarSyncTab from './components/CalendarSyncTab';
import CommunicationTab from './components/CommunicationTab';
import DigitalSignaturesTab from './components/DigitalSignaturesTab';

export interface IntegrationsPanelProps {
  onSave?: (config: any) => void;
  onCancel?: () => void;
}

type IntegrationSection = 
  | 'dashboard' 
  | 'email-storage' 
  | 'document-storage' 
  | 'api-keys'
  | 'digital-signatures'
  | 'court-systems' 
  | 'efiling' 
  | 'legal-research' 
  | 'accounting'
  | 'calendar-sync'
  | 'communication';

interface TabConfig {
  id: IntegrationSection;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  implemented: boolean;
  comingSoon?: string;
}

const TABS: TabConfig[] = [
  { 
    id: 'dashboard', 
    label: 'Dashboard',
    icon: LinkIcon,
    implemented: true
  },
  { 
    id: 'email-storage', 
    label: 'Email Storage',
    icon: EnvelopeIcon,
    implemented: true
  },
  { 
    id: 'document-storage', 
    label: 'Document Storage',
    icon: ServerIcon,
    implemented: true
  },
  { 
    id: 'api-keys', 
    label: 'API Keys',
    icon: KeyIcon,
    implemented: true
  },
  {
    id: 'digital-signatures',
    label: 'Digital Signatures',
    icon: ShieldCheckIcon,
    implemented: true
  },
  { 
    id: 'court-systems', 
    label: 'Court Systems',
    icon: BuildingLibraryIcon,
    implemented: false,
    comingSoon: 'Phase 3'
  },
  { 
    id: 'efiling', 
    label: 'E-Filing',
    icon: DocumentDuplicateIcon,
    implemented: false,
    comingSoon: 'Phase 3'
  },
  { 
    id: 'legal-research', 
    label: 'Legal Research',
    icon: MagnifyingGlassIcon,
    implemented: false,
    comingSoon: 'Phase 2'
  },
  { 
    id: 'accounting', 
    label: 'Accounting',
    icon: CalculatorIcon,
    implemented: false,
    comingSoon: 'Phase 2'
  },
  { 
    id: 'calendar-sync', 
    label: 'Calendar Sync',
    icon: CalendarIcon,
    implemented: false,
    comingSoon: 'Phase 2'
  },
  { 
    id: 'communication', 
    label: 'Communication',
    icon: ChatBubbleLeftRightIcon,
    implemented: false,
    comingSoon: 'Phase 2'
  }
];

/**
 * Integrations Panel Component
 * 
 * Centralized hub for managing all third-party integrations:
 * - Email Storage (Local, GCS, S3, Azure, NAS)
 * - Document Storage providers
 * - Court system connections
 * - E-Filing services
 * - Legal research platforms
 * - API key management
 * - Accounting software
 * - Calendar sync
 * - Communication tools
 */
export const IntegrationsPanel: React.FC<IntegrationsPanelProps> = ({ onSave }) => {
  const [activeSection, setActiveSection] = useState<IntegrationSection>('dashboard');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load initial data
  useEffect(() => {
    loadIntegrationData();
  }, []);

  const loadIntegrationData = async () => {
    setLoading(true);
    try {
      // TODO: Fetch integration status from backend
      setError(null);
    } catch (err) {
      setError('Failed to load integration data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // TODO: Save integration configuration
      if (onSave) {
        onSave({});
      }
    } catch (err) {
      setError('Failed to save configuration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
            <LinkIcon className="h-8 w-8 text-lex-accent mr-3" />
            Integrations
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Configure third-party connections, storage providers, and external services for your firm.
          </p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center space-x-2 ml-4">
          <button
            onClick={handleSave}
            disabled={loading}
            className="inline-flex items-center px-3 py-2 border border-lex-accent dark:border-lex-accent rounded-md text-sm font-medium text-lex-accent dark:text-lex-accent bg-lex-accent-soft dark:bg-lex-accent-soft hover:bg-lex-state-hover dark:hover:bg-lex-state-hover hover:border-lex-accent dark:hover:border-lex-accent transition-colors disabled:opacity-50"
            title="Save integration configuration"
          >
            <CogIcon className="h-4 w-4 mr-2" />
            Save Settings
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
        </div>
      )}

      {/* Navigation tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <nav className="-mb-px flex space-x-4 lg:space-x-8">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeSection === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => tab.implemented && setActiveSection(tab.id)}
                disabled={!tab.implemented}
                className={`
                  group inline-flex items-center py-3 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
                  ${isActive
                    ? 'border-lex-accent text-lex-accent dark:text-lex-accent'
                    : tab.implemented
                      ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      : 'border-transparent text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  }
                `}
                title={tab.implemented ? tab.label : `${tab.label} - Coming ${tab.comingSoon}`}
              >
                <Icon className={`h-5 w-5 mr-2 ${
                  isActive 
                    ? 'text-lex-accent dark:text-lex-accent' 
                    : tab.implemented
                      ? 'text-gray-400 group-hover:text-gray-500 dark:text-gray-500'
                      : 'text-gray-300 dark:text-gray-600'
                }`} />
                {tab.label}
                {!tab.implemented && tab.comingSoon && (
                  <span className="ml-2 inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400">
                    {tab.comingSoon}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content sections */}
      <div className="min-h-[500px]">
        {activeSection === 'dashboard' && <IntegrationsDashboard />}
        {activeSection === 'email-storage' && <EmailStorageTab />}
        {activeSection === 'document-storage' && <DocumentStorageTab />}
        {activeSection === 'api-keys' && <APIKeysTab />}
        {activeSection === 'digital-signatures' && <DigitalSignaturesTab />}
        {activeSection === 'court-systems' && <CourtSystemsTab />}
        {activeSection === 'efiling' && <EFilingTab />}
        {activeSection === 'legal-research' && <LegalResearchTab />}
        {activeSection === 'accounting' && <AccountingTab />}
        {activeSection === 'calendar-sync' && <CalendarSyncTab />}
        {activeSection === 'communication' && <CommunicationTab />}
      </div>
    </div>
  );
};

export default IntegrationsPanel;
